'use client';

import { useMemo, useState } from 'react';
import {
  Compass,
  Gift,
  Heart,
  PackageCheck,
  Rocket,
  Trash2,
  Trophy,
  X,
} from 'lucide-react';
import {
  FairtradeAvailabilityBadge,
  FairtradeCategoryBadge,
  FairtradeDaysValue,
  FairtradeXatsValue,
} from '@/components/help-center/fairtrade/fairtrade-shared';
import { GsImage, MoXat } from '@/components/help-center/latest-power-shared';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { usePowerCollection } from '@/contexts/PowerCollectionContext';
import {
  getFairtradeRowDisplayPrices,
  type FairtradeCollectionPriceStats,
  type FairtradePowerRow,
} from '@/lib/fairtrade-catalog';
import type { OwnedItem, WishItem } from '@/lib/power-collection-types';
import { formatPowerDisplayName } from '@/lib/power-mention-data';
import { cn } from '@/lib/utils';
import type { ComponentType, CSSProperties } from 'react';

type PowerJourneyDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  catalogRows: FairtradePowerRow[];
  collectionPrices?: Map<string, FairtradeCollectionPriceStats>;
};

type PriceSnapshot = {
  minXats: number;
  maxXats: number;
  minDays: number;
  maxDays: number;
};

function formatXatsAmount(n: number): string {
  if (!n) return '—';
  return new Intl.NumberFormat('pt-BR').format(Math.round(n));
}

function catalogLookup(
  catalogById: Map<number, FairtradePowerRow>,
  powerId: number,
  collectionPrices?: Map<string, FairtradeCollectionPriceStats>
): PriceSnapshot | null {
  const row = catalogById.get(powerId);
  if (!row) return null;
  return getFairtradeRowDisplayPrices(row, collectionPrices);
}

function resolveDisplayName(
  storedName: string,
  shortname: string,
  catalogById: Map<number, FairtradePowerRow>,
  powerId: number
): string {
  const row = catalogById.get(powerId);
  const code = row?.name || shortname || storedName;
  return formatPowerDisplayName(code);
}

function exploreFairtrade(onClose: () => void) {
  onClose();
  requestAnimationFrame(() => {
    document
      .getElementById('fairtrade-powers-panel')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
}

export default function PowerJourneyDialog({
  open,
  onOpenChange,
  catalogRows,
  collectionPrices,
}: PowerJourneyDialogProps) {
  const {
    wishes,
    owned,
    stats,
    removeWish,
    markOwnedFromWish,
    removeOwned,
  } = usePowerCollection();
  const [tab, setTab] = useState<'desejos' | 'conquistados'>('desejos');

  const catalogById = useMemo(() => {
    const map = new Map<number, FairtradePowerRow>();
    for (const row of catalogRows) map.set(row.id, row);
    return map;
  }, [catalogRows]);

  const close = () => onOpenChange(false);
  const wishNeedsScroll = wishes.length >= 5;
  const ownedNeedsScroll = owned.length >= 5;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className={cn(
          'flex max-h-[min(92vh,900px)] w-[min(96vw,52rem)] max-w-4xl flex-col gap-0 overflow-hidden',
          'border-2 border-solid bg-gradient-to-br from-[#2a0a18] via-[#1a0610] to-[#0c0408] p-0 text-rose-50',
          'shadow-2xl shadow-rose-950/50',
          'sm:rounded-2xl',
          'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95'
        )}
        style={{
          borderColor: 'rgba(251, 113, 133, 0.55)',
          boxShadow:
            '0 25px 50px -12px rgba(76, 5, 25, 0.55), 0 0 0 1px rgba(244, 63, 94, 0.2)',
        }}
      >
        <div
          className="pointer-events-none absolute inset-0 bg-gradient-to-br from-rose-500/15 via-transparent to-fuchsia-700/10"
          aria-hidden
        />
        <DialogClose
          className={cn(
            'absolute right-4 top-4 z-30 flex h-9 w-9 items-center justify-center rounded-lg',
            'border border-solid border-rose-400/25',
            'bg-gradient-to-br from-[#2a0a18]/95 via-[#1a0610]/95 to-[#0c0408]/95',
            'text-rose-200 shadow-[0_2px_10px_rgba(0,0,0,0.35),inset_0_1px_0_rgba(251,113,133,0.12)]',
            'backdrop-blur-sm transition-all duration-200',
            'hover:border-rose-400/40 hover:from-[#3d1224] hover:via-[#2a0a18] hover:to-[#1a0610]',
            'hover:[&_svg]:text-rose-100',
            'active:scale-95',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-400/50 focus-visible:ring-offset-2 focus-visible:ring-offset-[#1a0610]'
          )}
        >
          <X className="h-[17px] w-[17px] text-rose-200" strokeWidth={2.5} aria-hidden />
          <span className="sr-only">Fechar</span>
        </DialogClose>

        {/* Header */}
        <DialogHeader className="relative shrink-0 space-y-0 border-b border-rose-400/25 px-5 py-5 pr-14 text-left sm:px-6">
          <div className="flex items-start gap-3.5">
            <span
              className={cn(
                'relative inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl',
                'border border-solid border-rose-400/50',
                'bg-gradient-to-br from-rose-500/30 via-[#2a0a18] to-[#14040c]',
                'shadow-[0_0_20px_rgba(244,63,94,0.22)]'
              )}
            >
              <Heart
                className="h-5 w-5 fill-rose-400/90 text-rose-400/90"
                strokeWidth={1.5}
                aria-hidden
              />
            </span>
            <div className="min-w-0 space-y-1 pt-0.5">
              <DialogTitle className="font-poppins text-lg font-bold tracking-tight text-rose-100 sm:text-xl">
                Lista de desejos
              </DialogTitle>
              <DialogDescription className="font-inter text-sm leading-relaxed text-rose-200/70">
                Acompanhe seus powers desejados e sua evolução no Fairtrade.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Stats */}
        <div className="relative shrink-0 space-y-3 border-b border-rose-400/20 px-5 py-4 sm:px-6">
          <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-4">
            <JourneyStatCard
              theme="emerald"
              icon={Trophy}
              label="Conquistados"
              value={String(stats.ownedCount)}
              hint="Já na sua coleção"
            />
            <JourneyStatCard
              theme="rose"
              icon={Heart}
              label="Na minha lista"
              value={String(stats.wishCount)}
              hint="Powers que você deseja"
            />
            <JourneyStatCard
              theme="amber"
              icon={MoXatIcon}
              label="Valor estimado"
              value={
                stats.estimatedValueXats > 0
                  ? formatXatsAmount(stats.estimatedValueXats)
                  : '—'
              }
              suffix={stats.estimatedValueXats > 0 ? 'xats' : undefined}
              hint="Preços Fairtrade atuais"
            />
            <JourneyStatCard
              theme="violet"
              icon={Rocket}
              label="Progresso"
              value={`${stats.ownedCount}`}
              suffix={`/ ${stats.catalogTotal || '—'}`}
              hint="Coleção Fairtrade"
            />
          </div>

          {stats.catalogTotal > 0 ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <p className="font-poppins text-[10px] font-semibold uppercase tracking-[0.12em] text-rose-200/55">
                  Progresso da coleção
                </p>
                <p className="font-poppins text-[11px] font-semibold tabular-nums text-rose-100/80">
                  {stats.ownedCount}
                  <span className="font-medium text-rose-200/45"> / {stats.catalogTotal}</span>
                  <span className="ml-1.5 text-[10px] font-medium text-rose-200/40">
                    ({Math.round(stats.progress * 100)}%)
                  </span>
                </p>
              </div>
              <div className="relative h-4 overflow-hidden rounded-full border border-solid border-rose-400/30 bg-[#0c0408]/90 p-[3px] shadow-[inset_0_1px_3px_rgba(0,0,0,0.55)]">
                <div className="relative h-full overflow-hidden rounded-full bg-rose-950/60">
                  <div
                    className={cn(
                      'relative h-full rounded-full',
                      'bg-gradient-to-r from-rose-700 via-rose-400 to-pink-300',
                      'shadow-[0_0_18px_rgba(244,63,94,0.5)]',
                      'transition-[width] duration-700 ease-out'
                    )}
                    style={{
                      width: `${Math.min(100, Math.max(0, stats.progress * 100))}%`,
                    }}
                  >
                    <span
                      className="pointer-events-none absolute inset-x-0 top-0 h-1/2 rounded-full bg-gradient-to-b from-white/35 to-transparent"
                      aria-hidden
                    />
                    <span
                      className="pointer-events-none absolute inset-y-[2px] right-0 w-2 rounded-full bg-pink-100/50 blur-[1px]"
                      aria-hidden
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>

        {/* Tabs + scrollable list */}
        <Tabs
          value={tab}
          onValueChange={(v) => setTab(v as 'desejos' | 'conquistados')}
          className="relative flex min-h-0 flex-1 flex-col overflow-hidden"
        >
          <div className="shrink-0 border-b border-rose-400/20 px-5 pt-2 sm:px-6">
            <TabsList className="h-auto w-full justify-start gap-1 rounded-none bg-transparent p-0">
              <TabsTrigger
                value="desejos"
                className={cn(
                  'gap-1.5 rounded-t-lg rounded-b-none border-b-2 border-transparent px-3 py-2.5',
                  'font-poppins text-xs font-semibold uppercase tracking-wide text-rose-200/50',
                  'transition-colors duration-200',
                  'hover:bg-rose-500/10 hover:text-rose-100',
                  'data-[state=active]:border-rose-400 data-[state=active]:bg-rose-500/15 data-[state=active]:text-rose-50 data-[state=active]:shadow-none',
                  'data-[state=active]:hover:bg-rose-500/20'
                )}
              >
                <Heart className="h-3.5 w-3.5" aria-hidden />
                Desejos
                <span className="rounded-full bg-white/[0.08] px-1.5 py-0.5 text-[10px] tabular-nums">
                  {stats.wishCount}
                </span>
              </TabsTrigger>
              <TabsTrigger
                value="conquistados"
                className={cn(
                  'gap-1.5 rounded-t-lg rounded-b-none border-b-2 border-transparent px-3 py-2.5',
                  'font-poppins text-xs font-semibold uppercase tracking-wide text-rose-200/50',
                  'transition-colors duration-200',
                  'hover:bg-rose-500/10 hover:text-rose-100',
                  'data-[state=active]:border-rose-400 data-[state=active]:bg-rose-500/15 data-[state=active]:text-rose-50 data-[state=active]:shadow-none',
                  'data-[state=active]:hover:bg-rose-500/20'
                )}
              >
                <Gift className="h-3.5 w-3.5" aria-hidden />
                Conquistados
                <span className="rounded-full bg-white/[0.08] px-1.5 py-0.5 text-[10px] tabular-nums">
                  {stats.ownedCount}
                </span>
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent
            value="desejos"
            className={cn(
              'mt-0 min-h-0 flex-1 px-4 py-4 data-[state=inactive]:hidden sm:px-5',
              'fairtrade-wishlist-scrollbar',
              wishNeedsScroll
                ? 'max-h-[min(42vh,22.5rem)] overflow-y-auto pr-3'
                : 'overflow-y-visible'
            )}
          >
            {wishes.length === 0 ? (
              <JourneyEmptyState
                icon={Heart}
                title="Você ainda não adicionou nenhum power."
                description="Explore o Fairtrade e monte sua coleção."
                onExplore={() => exploreFairtrade(close)}
              />
            ) : (
              <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {wishes.map((wish, index) => (
                  <WishCollectionCard
                    key={wish.powerId}
                    displayName={resolveDisplayName(
                      wish.name,
                      wish.shortname,
                      catalogById,
                      wish.powerId
                    )}
                    wish={wish}
                    index={index}
                    limited={catalogById.get(wish.powerId)?.flags.limited ?? false}
                    prices={
                      catalogLookup(catalogById, wish.powerId, collectionPrices) ?? {
                        minXats: wish.savedMinXats,
                        maxXats: wish.savedMaxXats,
                        minDays: wish.savedMinDays,
                        maxDays: wish.savedMaxDays,
                      }
                    }
                    onRemove={() => void removeWish(wish.powerId)}
                    onMarkOwned={() => void markOwnedFromWish(wish)}
                  />
                ))}
              </ul>
            )}
          </TabsContent>

          <TabsContent
            value="conquistados"
            className={cn(
              'mt-0 min-h-0 flex-1 px-4 py-4 data-[state=inactive]:hidden sm:px-5',
              'fairtrade-wishlist-scrollbar',
              ownedNeedsScroll
                ? 'max-h-[min(42vh,22.5rem)] overflow-y-auto pr-3'
                : 'overflow-y-visible'
            )}
          >
            {owned.length === 0 ? (
              <JourneyEmptyState
                icon={Gift}
                title="Sua coleção ainda está vazia."
                description="Explore o Fairtrade e monte sua coleção."
                onExplore={() => exploreFairtrade(close)}
              />
            ) : (
              <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {owned.map((item, index) => (
                  <OwnedCollectionCard
                    key={item.powerId}
                    displayName={resolveDisplayName(
                      item.name,
                      item.shortname,
                      catalogById,
                      item.powerId
                    )}
                    item={item}
                    index={index}
                    limited={catalogById.get(item.powerId)?.flags.limited ?? false}
                    prices={
                      catalogLookup(catalogById, item.powerId, collectionPrices) ?? {
                        minXats: item.acquiredMinXats,
                        maxXats: item.acquiredMaxXats,
                        minDays: item.acquiredMinDays,
                        maxDays: item.acquiredMaxDays,
                      }
                    }
                    onRemove={() => void removeOwned(item.powerId)}
                  />
                ))}
              </ul>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function MoXatIcon({ className }: { className?: string }) {
  return <MoXat className={className} />;
}

type StatTheme = 'emerald' | 'rose' | 'amber' | 'violet';

const STAT_THEME: Record<
  StatTheme,
  { wrap: string; icon: string; glow: string; accent: string }
> = {
  emerald: {
    wrap: 'border-emerald-400/30 from-emerald-500/20',
    icon: 'bg-emerald-500/25 text-emerald-100 ring-emerald-400/30',
    glow: 'shadow-[0_0_22px_rgba(52,211,153,0.14)]',
    accent: 'from-emerald-400/30',
  },
  rose: {
    wrap: 'border-rose-400/30 from-rose-500/20',
    icon: 'bg-rose-500/25 text-rose-100 ring-rose-400/30',
    glow: 'shadow-[0_0_22px_rgba(244,63,94,0.14)]',
    accent: 'from-rose-400/30',
  },
  amber: {
    wrap: 'border-amber-400/30 from-amber-500/20',
    icon: 'bg-amber-500/25 text-amber-50 ring-amber-400/30',
    glow: 'shadow-[0_0_22px_rgba(251,191,36,0.14)]',
    accent: 'from-amber-400/30',
  },
  violet: {
    wrap: 'border-fuchsia-400/30 from-fuchsia-500/20',
    icon: 'bg-fuchsia-500/25 text-fuchsia-100 ring-fuchsia-400/30',
    glow: 'shadow-[0_0_22px_rgba(232,121,249,0.14)]',
    accent: 'from-fuchsia-400/30',
  },
};

function JourneyStatCard({
  theme,
  icon: Icon,
  label,
  value,
  suffix,
  hint,
}: {
  theme: StatTheme;
  icon: ComponentType<{ className?: string }>;
  label: string;
  value: string;
  suffix?: string;
  hint: string;
}) {
  const t = STAT_THEME[theme];
  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-2xl border border-solid bg-gradient-to-br to-transparent p-3.5',
        'bg-[#14040c]/55 backdrop-blur-sm',
        t.wrap,
        t.glow
      )}
    >
      <div
        className={cn(
          'pointer-events-none absolute -right-4 -top-4 h-16 w-16 rounded-full bg-gradient-to-br to-transparent opacity-60 blur-xl',
          t.accent
        )}
        aria-hidden
      />
      <div className="relative flex items-start gap-2.5">
        <span
          className={cn(
            'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ring-1',
            t.icon
          )}
        >
          <Icon className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <p className="font-poppins text-[9px] font-semibold uppercase tracking-wider text-rose-100/55">
            {label}
          </p>
          <p className="mt-1 font-poppins text-lg font-bold tabular-nums leading-none text-rose-50">
            {value}
            {suffix ? (
              <span className="ml-1 text-[10px] font-medium text-rose-100/45">{suffix}</span>
            ) : null}
          </p>
          <p className="mt-1.5 line-clamp-2 font-inter text-[10px] leading-snug text-rose-100/40">
            {hint}
          </p>
        </div>
      </div>
    </div>
  );
}

function JourneyEmptyState({
  icon: Icon,
  title,
  description,
  onExplore,
}: {
  icon: ComponentType<{ className?: string }>;
  title: string;
  description: string;
  onExplore: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-4 py-14 text-center">
      <span className="inline-flex h-16 w-16 items-center justify-center rounded-2xl border border-rose-400/30 bg-rose-500/15 shadow-[0_0_28px_rgba(244,63,94,0.2)]">
        <Icon className="h-7 w-7 fill-rose-300/80 text-rose-300/80" />
      </span>
      <div className="space-y-1.5">
        <p className="font-poppins text-sm font-semibold text-rose-50">{title}</p>
        <p className="max-w-sm font-inter text-xs leading-relaxed text-rose-100/55">
          {description}
        </p>
      </div>
      <button
        type="button"
        onClick={onExplore}
        className={cn(
          'mt-2 inline-flex items-center gap-2 rounded-xl border border-rose-400/40 px-4 py-2.5',
          'bg-rose-500/20 font-poppins text-xs font-semibold text-rose-50',
          'transition-all hover:border-rose-300/55 hover:bg-rose-400/30 active:scale-95'
        )}
      >
        <Compass className="h-3.5 w-3.5" aria-hidden />
        Explorar powers
      </button>
    </div>
  );
}

function collectionCardClass(index: number) {
  const delay = Math.min(index, 6) * 40;
  return {
    className: cn(
      'group relative flex h-full flex-col overflow-hidden rounded-2xl border border-solid border-rose-400/30 p-3.5',
      'bg-gradient-to-br from-rose-500/[0.12] via-[#1a0610]/90 to-[#0c0408]/95 backdrop-blur-sm',
      'shadow-[inset_0_1px_0_rgba(255,228,230,0.06)]',
      'transition-[border-color,box-shadow] duration-300',
      'hover:border-rose-300/50 hover:shadow-[0_8px_28px_rgba(0,0,0,0.35),0_0_20px_rgba(244,63,94,0.1)]',
      'animate-in fade-in fill-mode-both'
    ),
    style: {
      animationDelay: `${delay}ms`,
    } as CSSProperties,
  };
}

function PowerSmiley({ code }: { code: string }) {
  return (
    <GsImage
      code={code}
      sizePx={40}
      className="flex h-10 w-10 shrink-0 items-center justify-center"
      imgClassName="max-h-10 max-w-10 object-contain"
    />
  );
}

function PowerPriceBlock({ prices }: { prices: PriceSnapshot }) {
  return (
    <div className="mt-3 grid grid-cols-2 gap-2">
      <div className="min-w-0 overflow-hidden rounded-xl border border-solid border-rose-400/25 bg-[#0c0408]/70 px-2 py-2">
        <FairtradeXatsValue
          min={prices.minXats}
          max={prices.maxXats}
          isDark
          className="!gap-1 !text-[11px] [&_span]:!text-[10px]"
        />
      </div>
      <div className="min-w-0 overflow-hidden rounded-xl border border-solid border-rose-400/25 bg-[#0c0408]/70 px-2 py-2">
        <FairtradeDaysValue
          min={prices.minDays}
          max={prices.maxDays}
          isDark
          className="!gap-1 !text-[11px] [&_span]:!text-[10px]"
        />
      </div>
    </div>
  );
}

function WishCollectionCard({
  wish,
  displayName,
  index,
  prices,
  limited,
  onRemove,
  onMarkOwned,
}: {
  wish: WishItem;
  displayName: string;
  index: number;
  prices: PriceSnapshot;
  limited: boolean;
  onRemove: () => void;
  onMarkOwned: () => void;
}) {
  const card = collectionCardClass(index);

  return (
    <li className={card.className} style={card.style}>
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex items-start gap-3">
          <PowerSmiley code={wish.shortname} />
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate font-poppins text-sm font-semibold text-rose-100">
                  {displayName}
                </p>
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  <FairtradeCategoryBadge
                    type={wish.category}
                    typeLabel={wish.typeLabel}
                    category={wish.category}
                  />
                  <FairtradeAvailabilityBadge limited={limited} isDark />
                </div>
              </div>
              <button
                type="button"
                onClick={onRemove}
                className="shrink-0 rounded-lg p-1.5 text-rose-200/55 transition-colors hover:bg-rose-500/20 hover:text-rose-50"
                aria-label="Remover dos desejos"
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden />
              </button>
            </div>
          </div>
        </div>

        <PowerPriceBlock prices={prices} />

        <button
          type="button"
          onClick={onMarkOwned}
          className={cn(
            'mt-auto inline-flex w-full items-center justify-center gap-1.5 rounded-xl border border-solid px-3 py-2.5',
            'mt-3 border-emerald-400/40 bg-emerald-500/15 font-poppins text-xs font-semibold text-emerald-100',
            'transition-all hover:border-emerald-300/55 hover:bg-emerald-400/25',
            'active:scale-[0.98]'
          )}
        >
          <PackageCheck className="h-3.5 w-3.5" aria-hidden />
          Tenho este power
        </button>
      </div>
    </li>
  );
}

function OwnedCollectionCard({
  item,
  displayName,
  index,
  prices,
  limited,
  onRemove,
}: {
  item: OwnedItem;
  displayName: string;
  index: number;
  prices: PriceSnapshot;
  limited: boolean;
  onRemove: () => void;
}) {
  const card = collectionCardClass(index);

  return (
    <li className={card.className} style={card.style}>
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex items-start gap-3">
          <PowerSmiley code={item.shortname} />
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate font-poppins text-sm font-semibold text-rose-100">
                  {displayName}
                </p>
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  <FairtradeCategoryBadge
                    type={item.category}
                    typeLabel={item.typeLabel}
                    category={item.category}
                  />
                  <FairtradeAvailabilityBadge limited={limited} isDark />
                </div>
              </div>
              <button
                type="button"
                onClick={onRemove}
                className="shrink-0 rounded-lg p-1.5 text-rose-200/55 transition-colors hover:bg-rose-500/20 hover:text-rose-50"
                aria-label="Remover da coleção"
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden />
              </button>
            </div>
          </div>
        </div>

        <PowerPriceBlock prices={prices} />
      </div>
    </li>
  );
}
